import './purchase-selection.css';

import { useState } from 'react';
import { ShoppingCart, Plus, Minus } from 'lucide-react';

import { Reward2x1 } from '../bonus';
import {
  useLeague,
  useAuth,
  useBilling,
  useLeagueSeasonTimer,
} from '../../../hooks';
import { stripePromise } from '../../../services';
import { createCheckoutSession } from '../../../firebase';

interface PurchaseSectionProps {
  onPurchase?: (quantity: number) => void;
}

export function PurchaseSection({ onPurchase }: PurchaseSectionProps) {
  const { timeRemaining } = useLeagueSeasonTimer();
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(false);

  const { user } = useAuth();
  const { league, currentSeason } = useLeague();
  const { calculate } = useBilling();

  const has2x1 = currentSeason?.bonus?.includes('2x1') ?? false;

  const cardPrice = Number(currentSeason?.value ?? 0);
  const subtotalBase = cardPrice * quantity;

  const { gross, tax, taxRate, net, stripeFee } = calculate(subtotalBase);

  const handlePurchase = async () => {
    if (!user || !league || !currentSeason) {
      return;
    }

    try {
      setLoading(true);

      const data = await createCheckoutSession({
        quantity,
        userId: user.uid,
        leagueId: league.id,
        seasonId: String(currentSeason.seasonNumber),
        has2x1,
      });

      if (!data.url) {
        throw new Error('No session URL');
      }

      const stripe = await stripePromise;

      if (!stripe) {
        throw new Error('Stripe not loaded');
      }

      window.location.href = data.url;

      onPurchase?.(quantity);
    } catch (error) {
      alert('Error al iniciar el pago');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {!timeRemaining.finished && (
        <div className="purchase-section">
          <div className="purchase-header">
            <ShoppingCart className="purchase-icon-main" stroke="#CAFB10" />
            <h2 className="purchase-title">Comprar Tarjetas</h2>
          </div>

          <div className="purchase-quantity">
            <label className="purchase-label">
              Cantidad de tarjetas a comprar:
            </label>

            <div className="purchase-controls">
              <button
                onClick={() => setQuantity((prev) => Math.max(1, prev - 1))}
                className="purchase-btn"
                disabled={quantity <= 1 || loading}
              >
                <Minus className="icon" />
              </button>

              <input
                type="number"
                min="1"
                max="100"
                value={quantity}
                onChange={(e) =>
                  setQuantity(Math.max(1, parseInt(e.target.value || '1', 10)))
                }
                className="purchase-input"
                disabled={loading}
              />

              <button
                onClick={() => setQuantity((prev) => prev + 1)}
                className="purchase-btn"
                disabled={loading}
              >
                <Plus className="icon" />
              </button>
            </div>
          </div>

          <Reward2x1 quantity={quantity} />

          <div className="purchase-breakdown">
            <div className="row">
              <span>
                Subtotal ({quantity} × €{cardPrice.toFixed(2)})
              </span>
              <span className="mono">€{net.toFixed(2)}</span>
            </div>

            <div className="divider" />

            <div className="row small">
              <span>Gastos de gestión</span>
              <span className="mono">€{stripeFee.toFixed(2)}</span>
            </div>

            <div className="row small">
              <span>IVA ({(taxRate * 100).toFixed(0)}%)</span>
              <span className="mono">€{tax.toFixed(2)}</span>
            </div>

            <div className="divider strong" />

            <div className="row total">
              <span>Total a pagar:</span>
              <span className="mono highlight">€{gross.toFixed(2)}</span>
            </div>
          </div>

          <button
            onClick={handlePurchase}
            className="purchase-button"
            disabled={loading || cardPrice <= 0}
          >
            <ShoppingCart className="icon" />
            {loading ? 'Procesando...' : 'Proceder al Pago'}
          </button>
        </div>
      )}
    </>
  );
}
